import { useEffect, useState } from "react";
import { apiGet, apiPost } from "@/lib/api";
import api, { apiDelete } from "@/lib/api";
import { Plus, Check, X, Home, Search, ChevronLeft, ChevronRight, Trash2, KeyRound, Car } from "lucide-react";

const TABS = ["All", "Pending", "Active", "Suspended"];
const PAGE_SIZE = 10;
const HOUSE_NUMBERS = Array.from({ length: 44 }, (_, i) => String(i + 1));
const STREETS = ["JALAN 1/1","JALAN 1/2","JALAN 1/3","JALAN 1/4","JALAN 1/5","JALAN 1/6","JALAN 1/7","JALAN 1/8"];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-MY", { month: "short", year: "numeric" });
}

function vehicleTypeLabel(type) {
  if (type === "Sticker fee - Car") return "Car";
  if (type === "Sticker fee - Motorcycle") return "Motorcycle";
  return type || "Vehicle";
}

function VehiclePlatesButton({ resident }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plates, setPlates] = useState(null);

  const alias = resident.house_alias || resident.username || "";

  async function openModal() {
    setOpen(true);
    if (plates != null) return;
    if (!alias) {
      setPlates([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiGet(`/vehicle-plates?house_alias=${encodeURIComponent(alias)}`);
      const rows = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setPlates(rows);
    } catch (e) {
      setPlates([]);
    } finally {
      setLoading(false);
    }
  }

  function closeModal() {
    setOpen(false);
  }

  return (
    <>
      <button
        type="button"
        className="btn"
        onClick={openModal}
        data-testid={`resident-vehicles-${resident.id}`}
        title="Registered vehicles"
      >
        <Car size={11} /> Vehicles
      </button>
      {open && (
        <div className="modal-overlay" onClick={closeModal} data-testid={`resident-vehicles-overlay-${resident.id}`}>
          <div
            className="modal-box"
            data-testid={`resident-vehicles-modal-${resident.id}`}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 480 }}
          >
            <div className="modal-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span>Registered Vehicles</span>
              <button type="button" className="btn" onClick={closeModal} aria-label="Close" data-testid={`close-resident-vehicles-${resident.id}`}>
                <X size={14} />
              </button>
            </div>
            <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 14 }}>
              {resident.name}
              {alias ? <> · House <span className="mono" style={{ fontWeight: 600, color: "var(--text)" }}>{alias}</span></> : null}
            </div>
            {loading ? (
              <div style={{ padding: "20px 0", textAlign: "center", fontSize: 12, color: "var(--text2)" }}>Loading vehicles...</div>
            ) : !plates || plates.length === 0 ? (
              <div style={{ padding: "20px 0", textAlign: "center", fontSize: 13, color: "var(--text2)" }}>N/A</div>
            ) : (
              <div style={{ overflowX: "auto", marginBottom: 14 }}>
                <table className="percasa-table">
                  <thead>
                    <tr>
                      <th>Plate</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plates.map((p) => (
                      <tr key={p.id || p.vehicle_plate_no}>
                        <td><span className="mono" style={{ fontWeight: 600 }}>{p.vehicle_plate_no}</span></td>
                        <td>{vehicleTypeLabel(p.type)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="modal-actions">
              <button type="button" className="btn" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function Residents() {
  const [residents, setResidents] = useState([]);
  const [tab, setTab] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Add resident
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", unit: "", street_number: "", phone: "" });
  const [addError, setAddError] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete / Reset password
  const [actionModal, setActionModal] = useState(null); // { type: 'delete'|'reset', resident }
  const [adminPw, setAdminPw] = useState("");
  const [newPw, setNewPw] = useState("Resident@123");
  const [actionError, setActionError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Assign house
  const [assignResident, setAssignResident] = useState(null);
  const [units, setUnits] = useState([]);
  const [assignUnit, setAssignUnit] = useState("");
  const [assignSearch, setAssignSearch] = useState("");
  const [assigning, setAssigning] = useState(false);

  useEffect(() => { fetchResidents(); }, []);

  async function fetchResidents() {
    setLoading(true);
    try {
      const res = await apiGet("/residents");
      setResidents(res.data);
    } catch (e) {}
    setLoading(false);
  }

  async function fetchUnits() {
    if (units.length > 0) return;
    try {
      const res = await apiGet("/units");
      setUnits(res.data);
    } catch (e) {}
  }

  async function approve(id) {
    try {
      await api(`/residents/${id}/approve`, { method: "POST" });
      setResidents(prev => prev.map(r => r.id === id ? { ...r, status: "active" } : r));
    } catch (e) {}
  }

  async function reject(id) {
    try {
      await api(`/residents/${id}/reject`, { method: "POST" });
      setResidents(prev => prev.map(r => r.id === id ? { ...r, status: "rejected" } : r));
    } catch (e) {}
  }

  async function suspend(id) {
    try {
      await api(`/residents/${id}/suspend`, { method: "POST" });
      setResidents(prev => prev.map(r => r.id === id ? { ...r, status: "suspended" } : r));
    } catch (e) {}
  }

  async function handleAction(e) {
    e.preventDefault();
    setActionError(""); setActionLoading(true);
    const r = actionModal.resident;
    try {
      if (actionModal.type === "delete") {
        await apiDelete(`/residents/${r.id}`, { admin_password: adminPw });
        setResidents(prev => prev.filter(x => x.id !== r.id));
      } else {
        const res = await api(`/residents/${r.id}/reset-password`, { method: "POST", data: { admin_password: adminPw, new_password: newPw } });
        alert(`Password reset to: ${res.data.temp_password}`);
      }
      setActionModal(null); setAdminPw(""); setNewPw("Resident@123");
    } catch (err) {
      setActionError(err.response?.data?.detail || "Operation failed");
    } finally { setActionLoading(false); }
  }

  async function addResident(e) {
    e.preventDefault();
    setAddError(""); setSaving(true);
    try {
      await apiPost("/residents", addForm);
      setShowAdd(false);
      setAddForm({ name: "", email: "", unit: "", street_number: "", phone: "" });
      await fetchResidents();
    } catch (err) {
      setAddError(err.response?.data?.detail || "Failed to add resident");
    } finally { setSaving(false); }
  }

  async function openAssign(r) {
    setAssignResident(r);
    setAssignUnit(r.unit || "");
    setAssignSearch("");
    await fetchUnits();
  }

  async function saveAssign(e) {
    e.preventDefault();
    setAssigning(true);
    try {
      await api(`/residents/${assignResident.id}`, { method: "PUT", data: { unit: assignUnit } });
      setResidents(prev => prev.map(r => r.id === assignResident.id ? { ...r, unit: assignUnit } : r));
      setAssignResident(null);
    } catch (e) {} finally { setAssigning(false); }
  }

  const filtered = residents.filter(r => {
    const matchTab = tab === "All" || r.status === tab.toLowerCase();
    const q = search.toLowerCase();
    const matchSearch = !q || r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q) || r.username?.toLowerCase().includes(q) || r.unit?.toLowerCase().includes(q);
    return matchTab && matchSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const counts = {
    All: residents.length,
    Pending: residents.filter(r => r.status === "pending").length,
    Active: residents.filter(r => r.status === "active").length,
    Suspended: residents.filter(r => r.status === "suspended").length,
  };

  const filteredUnits = units.filter(u =>
    !assignSearch || u.unit_number?.toLowerCase().includes(assignSearch.toLowerCase())
  );

  function renderActions(r) {
    return (
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {r.status === "pending" && (
          <>
            <button className="btn btn-approve" onClick={() => approve(r.id)} data-testid={`approve-resident-${r.id}`}><Check size={11} /> Approve</button>
            <button className="btn btn-reject"  onClick={() => reject(r.id)}  data-testid={`reject-resident-${r.id}`}><X size={11} /> Reject</button>
          </>
        )}
        {r.status === "active" && (
          <button className="btn" onClick={() => suspend(r.id)} data-testid={`suspend-resident-${r.id}`}>Suspend</button>
        )}
        {r.status === "suspended" && (
          <button className="btn btn-approve" onClick={() => approve(r.id)} data-testid={`reactivate-resident-${r.id}`}>Reactivate</button>
        )}
        {r.status === "rejected" && (
          <span style={{ fontSize: 10, color: "var(--text2)" }}>Rejected</span>
        )}
        <button className="btn" onClick={() => openAssign(r)} style={{ color: "var(--brand)" }} data-testid={`assign-house-${r.id}`}>
          <Home size={11} /> Assign
        </button>
        <button className="btn" style={{ color: "var(--text2)" }} title="Reset password"
          onClick={() => { setActionModal({ type: "reset", resident: r }); setAdminPw(""); setNewPw("Resident@123"); setActionError(""); }}>
          <KeyRound size={11} />
        </button>
        <button className="btn" style={{ color: "var(--danger)" }} title="Delete resident"
          onClick={() => { setActionModal({ type: "delete", resident: r }); setAdminPw(""); setActionError(""); }}>
          <Trash2 size={11} />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="section-panel" data-testid="residents-panel">
        <div className="section-head">
          <div className="section-title">Resident Management</div>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="add-resident-btn">
            <Plus size={13} /> Add Resident
          </button>
        </div>

        <div className="tabs" style={{ flexWrap: "wrap", gap: 4 }}>
          {TABS.map(t => (
            <div key={t} className={`tab ${tab === t ? "active" : ""}`}
              onClick={() => { setTab(t); setPage(1); }}
              data-testid={`tab-${t.toLowerCase()}`}>
              {t} ({counts[t]})
            </div>
          ))}
          <div className="head-search" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <div className="head-search-box" style={{ position: "relative" }}>
              <Search size={11} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--text2)" }} />
              <input
                className="form-input head-search-input"
                style={{ paddingLeft: 26, fontSize: 11, height: 28, width: 180 }}
                placeholder="Search name / email / house…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                data-testid="residents-search"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: "24px", textAlign: "center", color: "var(--text2)", fontSize: 12 }}>Loading residents...</div>
        ) : (
          <>
          {/* Desktop / tablet table */}
          <div className="payments-table-wrap" style={{ overflowX: "auto" }}>
            <table className="percasa-table">
              <thead>
                <tr>
                  <th>Name</th><th>House No.</th><th>Username</th>
                  <th>Outstanding</th><th>Vehicles</th><th>Status</th><th>Joined</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageData.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", color: "var(--text2)", padding: "24px" }}>No residents found</td></tr>
                ) : pageData.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 500 }}>{r.name}</td>
                    <td>
                      <span className="mono">{r.unit || "—"}</span>
                      {r.street_number && <div style={{ fontSize: 9, color: "var(--text2)" }}>{r.street_number}</div>}
                    </td>
                    <td style={{ fontSize: 11 }}>
                      <span className="mono" style={{ fontWeight: 600 }}>{r.username || "—"}</span>
                      {r.email && !r.email.endsWith("@percasa.local") && (
                        <div style={{ fontSize: 9, color: "var(--text2)" }}>{r.email}</div>
                      )}
                    </td>
                    <td>
                      {r.status === "active" && r.outstanding_months > 0 ? (
                        <div>
                          <span className={`badge ${r.outstanding_months >= 3 ? "rejected" : "pending"}`}>
                            {r.outstanding_months} {r.outstanding_months === 1 ? "month" : "months"}
                          </span>
                          <div className="mono" style={{ fontSize: 10, color: "var(--danger)", marginTop: 4 }}>
                            RM {r.outstanding_amount?.toFixed(2)}
                          </div>
                        </div>
                      ) : (
                        <span style={{ fontSize: 10, color: "var(--text2)" }}>
                          {r.status === "active" ? "Up to date" : "—"}
                        </span>
                      )}
                    </td>
                    <td><VehiclePlatesButton resident={r} /></td>
                    <td><span className={`badge ${r.status}`}>{capitalize(r.status)}</span></td>
                    <td style={{ color: "var(--text2)", fontSize: 11 }}>{fmtDate(r.created_at)}</td>
                    <td>{renderActions(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="payments-card-list" data-testid="residents-card-list">
            {pageData.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>No residents found</div>
            ) : pageData.map(r => (
              <div key={r.id} className="payment-card">
                <div className="payment-card-top">
                  <span style={{ fontWeight: 600 }}>{r.name}</span>
                  <span className={`badge ${r.status}`}>{capitalize(r.status)}</span>
                </div>
                <div className="payment-card-row">
                  <span>House <span className="mono" style={{ fontWeight: 600 }}>{r.unit || "—"}</span>{r.street_number ? ` · ${r.street_number}` : ""}</span>
                  <span className="mono">{r.username || "—"}</span>
                </div>
                <div className="payment-card-meta">
                  {r.email && !r.email.endsWith("@percasa.local") && <span>{r.email}</span>}
                  <span>Joined {fmtDate(r.created_at)}</span>
                </div>
                <div className="payment-card-meta">
                  {r.status === "active" && r.outstanding_months > 0 ? (
                    <span style={{ color: "var(--danger)" }}>
                      Outstanding: {r.outstanding_months} {r.outstanding_months === 1 ? "month" : "months"} · RM {r.outstanding_amount?.toFixed(2)}
                    </span>
                  ) : r.status === "active" ? (
                    <span style={{ color: "var(--success)" }}>Payments up to date</span>
                  ) : null}
                </div>
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-start" }}>
                  <VehiclePlatesButton resident={r} />
                  {renderActions(r)}
                </div>
              </div>
            ))}
          </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 18px", borderTop: "1px solid var(--border)", fontSize: 11 }}>
            <span style={{ color: "var(--text2)" }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <button className="btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="residents-prev-page">
                <ChevronLeft size={12} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
                <button key={pg} className={`btn ${pg === page ? "btn-primary" : ""}`} style={{ minWidth: 28 }}
                  onClick={() => setPage(pg)} data-testid={`residents-page-${pg}`}>
                  {pg}
                </button>
              ))}
              <button className="btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="residents-next-page">
                <ChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Resident Modal */}
      {showAdd && (
        <div className="modal-overlay">
          <div className="modal-box" data-testid="add-resident-modal">
            <div className="modal-title">Add New Resident</div>
            {addError && <div className="auth-error">{addError}</div>}
            <form onSubmit={addResident}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input className="form-input" placeholder="Ahmad Hisham" value={addForm.name} onChange={e => setAddForm(f => ({...f, name: e.target.value}))} required data-testid="add-name-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" placeholder="ahmad@email.com" value={addForm.email} onChange={e => setAddForm(f => ({...f, email: e.target.value}))} required data-testid="add-email-input" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">House Number</label>
                  <select className="form-input" value={addForm.unit} onChange={e => setAddForm(f => ({...f, unit: e.target.value}))} data-testid="add-unit-input">
                    <option value="">— Select —</option>
                    {HOUSE_NUMBERS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Street</label>
                  <select className="form-input" value={addForm.street_number} onChange={e => setAddForm(f => ({...f, street_number: e.target.value}))} data-testid="add-street-input">
                    <option value="">— Select —</option>
                    {STREETS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row" style={{ marginTop: 0 }}>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" placeholder="+60 12-345 6789" value={addForm.phone} onChange={e => setAddForm(f => ({...f, phone: e.target.value}))} data-testid="add-phone-input" />
                </div>
              </div>
              <div style={{ fontSize: 10, color: "var(--text2)", marginBottom: 12 }}>
                Default password: <span className="mono">Resident@123</span>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => setShowAdd(false)} data-testid="cancel-add-resident-btn">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving} data-testid="save-add-resident-btn">
                  {saving ? "Adding..." : "Add Resident"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete / Reset Password Confirmation Modal */}
      {actionModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 420 }}>
            <div className="modal-title" style={{ color: actionModal.type === "delete" ? "var(--danger)" : "var(--brand)" }}>
              {actionModal.type === "delete" ? <><Trash2 size={14} style={{ marginRight: 6 }} />Delete Resident</> : <><KeyRound size={14} style={{ marginRight: 6 }} />Reset Password</>}
            </div>
            {actionModal.type === "delete" ? (
              <div style={{ fontSize: 13, marginBottom: 14, color: "var(--text2)" }}>
                Permanently delete <b>{actionModal.resident.name}</b>? This will remove all their payment records and cannot be undone.
              </div>
            ) : (
              <div style={{ fontSize: 13, marginBottom: 14, color: "var(--text2)" }}>
                Reset password for <b>{actionModal.resident.name}</b>.
              </div>
            )}
            {actionError && <div className="auth-error" style={{ marginBottom: 10 }}>{actionError}</div>}
            <form onSubmit={handleAction}>
              {actionModal.type === "reset" && (
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input className="form-input" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Min. 6 characters" required minLength={6} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Your Admin Password <span style={{ color: "var(--danger)" }}>*</span></label>
                <input type="password" className="form-input" value={adminPw} onChange={e => setAdminPw(e.target.value)} placeholder="Enter your admin password to confirm" required autoFocus />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => { setActionModal(null); setAdminPw(""); }}>Cancel</button>
                <button type="submit" className="btn" disabled={actionLoading}
                  style={{ background: actionModal.type === "delete" ? "var(--danger)" : "var(--brand)", color: "#fff" }}>
                  {actionLoading ? "Processing..." : actionModal.type === "delete" ? "Delete" : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign House Modal */}
      {assignResident && (
        <div className="modal-overlay">
          <div className="modal-box" data-testid="assign-house-modal">
            <div className="modal-title">Assign House — {assignResident.name}</div>
            <form onSubmit={saveAssign}>
              <div className="form-group">
                <label className="form-label">Search House Number</label>
                <div style={{ position: "relative" }}>
                  <Search size={11} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--text2)" }} />
                  <input
                    className="form-input"
                    style={{ paddingLeft: 26 }}
                    placeholder="e.g. A-01"
                    value={assignSearch}
                    onChange={e => setAssignSearch(e.target.value)}
                    data-testid="assign-search-input"
                  />
                </div>
              </div>
              <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 5, marginBottom: 14 }}>
                {filteredUnits.length === 0 ? (
                  <div style={{ padding: 16, textAlign: "center", fontSize: 11, color: "var(--text2)" }}>No houses found</div>
                ) : filteredUnits.slice(0, 50).map(u => (
                  <div key={u.id}
                    onClick={() => setAssignUnit(u.unit_number)}
                    style={{
                      padding: "8px 14px", cursor: "pointer", fontSize: 12,
                      background: assignUnit === u.unit_number ? "var(--brand-light)" : "transparent",
                      borderLeft: assignUnit === u.unit_number ? "2px solid var(--brand)" : "2px solid transparent",
                      display: "flex", justifyContent: "space-between", alignItems: "center"
                    }}
                    data-testid={`select-unit-${u.unit_number}`}
                  >
                    <span className="mono">{u.unit_number}</span>
                    <span className={`badge ${u.status}`}>{capitalize(u.status)}</span>
                  </div>
                ))}
              </div>
              {assignUnit && (
                <div style={{ fontSize: 11, color: "var(--brand)", marginBottom: 14 }}>
                  Selected: <strong className="mono">{assignUnit}</strong>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => setAssignResident(null)} data-testid="cancel-assign-btn">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={assigning || !assignUnit} data-testid="save-assign-btn">
                  {assigning ? "Saving..." : "Assign House"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; }
